"use client";

import { MenuItem } from "react-aria-components";
import { AvatarWithTooltip, Text } from "@zapier/design-system";
import { Icon, type IconName } from "@zapier/zinnia-icons";
import styles from "./SearchDropdown.module.css";

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function getInitials(name?: string | null): string | undefined {
  if (!name) return undefined;
  const parts = name.trim().split(/\s+/);
  if (!parts[0]?.[0]) return undefined;
  return parts.length === 1 ? parts[0][0] : `${parts[0][0]}${parts.at(-1)![0]}`;
}

interface SearchResultProps {
  /** Stable id for the React Aria collection item. */
  id: string;
  title: string;
  query?: string;
  icon?: IconName;
  iconColor?: string;
  appIcon?: string;
  href: string;
  isApp?: boolean;
  updatedAt?: string | null;
  folderName?: string;
  ownerName?: string;
  ownerPhotoUrl?: string;
  /** Fired when the item is activated. */
  onAction?: () => void;
}

function highlightMatch(text: string, query?: string): React.ReactNode {
  if (!query?.trim()) return text;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.highlight}>
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  );
}

/**
 * A single search result rendered as a React Aria `MenuItem`. The item is a
 * link (`href`), so React Aria exposes it as a `menuitem` with the correct
 * focus semantics and screen-reader announcements.
 *
 * CRIT-3050 used to be worked around here by passing the callback as
 * `onPress` instead of `onAction`. That is no longer correct and the reason is
 * worth keeping: the workaround existed because these items lived in a
 * `ComboBox`'s listbox, which runs with `linkBehavior: "selection"`, where an
 * item carrying `onAction` is treated as an action item -- React Aria called
 * the handler and skipped navigation, so clicking a result did nothing. A
 * `Menu` runs with `linkBehavior: "action"`, where `onAction` both fires and
 * navigates, while `onPress` fires twice per click. So the workaround inverts
 * with the pattern: `onAction` here, `onPress` in a combobox listbox.
 */
export function SearchResult({
  id,
  title,
  query,
  icon,
  iconColor,
  appIcon,
  href,
  isApp = false,
  updatedAt,
  folderName,
  ownerName,
  ownerPhotoUrl,
  onAction,
}: SearchResultProps) {
  return (
    <MenuItem
      id={id}
      textValue={title}
      href={href}
      onAction={onAction}
      className={isApp ? styles.resultLink : styles.resultLinkVertical}
    >
      <div className={styles.resultIcon}>
        {appIcon ? (
          <img src={appIcon} alt="" width="16" height="16" />
        ) : (
          <Icon
            name={icon ?? "miscBoltAltFill"}
            size={16}
            color={(iconColor as any) ?? "UiPrimary"}
            isBlock
          />
        )}
      </div>
      <div className={styles.resultContent}>
        <div className={styles.resultTextContainer}>
          <div className={styles.resultTitle}>
            <Text tag="p" type="Body1Multiline" color="TextDefault">
              {highlightMatch(title, query)}
            </Text>
          </div>
          {(folderName || updatedAt) && (
            <span className={styles.resultMeta}>
              {folderName && (
                <Icon name="navFolder" size={12} color="TextWeakest" isBlock />
              )}
              <Text type="MinimalPrint1" color="TextWeakest">
                {[
                  folderName,
                  updatedAt ? formatTimeAgo(new Date(updatedAt)) : undefined,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            </span>
          )}
        </div>
        {ownerName && (
          <div className={styles.resultOwner}>
            <AvatarWithTooltip
              initials={getInitials(ownerName)}
              name={`Owner: ${ownerName}`}
              size="small"
              url={ownerPhotoUrl}
            />
          </div>
        )}
      </div>
    </MenuItem>
  );
}
